import type { Location } from '__types__';
import type {
  FieldCodec,
  LocationValue
} from 'v5/core/entity-values/values.types';
import { validateField, validation } from './validation';

/** A stored location as a row of the locations list shows it. */
export function locationValueOf(location: Location): LocationValue {
  const lat = location.coordinates?.lat;
  const lon = location.coordinates?.lon;
  return {
    name: location.name ?? '',
    coordinates: lat != null && lon != null ? { lat, lon } : null
  };
}

export function sameLocation(a: LocationValue, b: LocationValue): boolean {
  return (
    a.name === b.name &&
    a.coordinates?.lat === b.coordinates?.lat &&
    a.coordinates?.lon === b.coordinates?.lon
  );
}

function isEmpty(value: LocationValue): boolean {
  return value.name.trim() === '';
}

/**
 * One row of the resource's locations (a list unit on the entity): read from
 * the location at the row's index. The list as a whole is written by
 * `encodeList` (related by id, or created), never one row alone.
 */
export const locationCodec: FieldCodec<'location'> = {
  decode(entity, spec, ctx) {
    if (ctx.target.type !== 'locations-item') {
      throw new Error(
        `Location field "${spec.key}" can only be decoded from a locations item, got target "${ctx.target.type}"`
      );
    }
    const location = (entity.locations ?? [])[ctx.target.index];
    if (location === undefined) {
      throw new Error(
        `Location field "${spec.key}": no location at index ${ctx.target.index}`
      );
    }
    return locationValueOf(location);
  },
  encode(value) {
    return { type: 'location', value };
  },
  empty() {
    return { name: '', coordinates: null };
  },
  isEmpty,
  validate(value, spec) {
    return validateField(value, spec, isEmpty, validation.valid);
  }
};
